import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Calendar, MapPin, Weight, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";

interface Catch {
  id: string;
  fish_species: string;
  weight_lbs: number;
  date_caught: string;
  general_location: string;
  photo_url: string | null;
  status: string;
  video_url?: string;
  length_inches?: number;
  girth_inches?: number;
  angler_notes?: string;
}

interface CatchDetailModalProps {
  catch: Catch | null;
  isOpen: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

export const CatchDetailModal = ({
  catch: catchData,
  isOpen,
  onClose,
  onNext,
  onPrevious,
  hasNext,
  hasPrevious,
}: CatchDetailModalProps) => {
  if (!catchData) return null;

  const statusColors = {
    pending: "warning",
    verified: "success",
    rejected: "destructive",
  } as const;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="space-y-4">
          {/* Navigation buttons */}
          {(hasNext || hasPrevious) && (
            <div className="flex justify-between items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrevious}
                disabled={!hasPrevious}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onNext}
                disabled={!hasNext}
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}

          {/* Photo */}
          {catchData.photo_url && (
            <div className="w-full overflow-hidden rounded-lg">
              <img
                src={catchData.photo_url}
                alt={catchData.fish_species}
                className="w-full h-auto object-cover"
              />
            </div>
          )}

          {/* Header with species and status */}
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-3xl font-bold text-foreground">
              {catchData.fish_species}
            </h2>
            <Badge variant={statusColors[catchData.status as keyof typeof statusColors] as any}>
              {catchData.status}
            </Badge>
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Weight className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Weight</p>
                <p className="text-lg font-semibold">{catchData.weight_lbs} lbs</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Calendar className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Date Caught</p>
                <p className="text-lg font-semibold">
                  {format(new Date(catchData.date_caught), "MMM d, yyyy")}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 md:col-span-2">
              <MapPin className="h-5 w-5 text-primary" />
              <div>
                <p className="text-sm text-muted-foreground">Location</p>
                <p className="text-lg font-semibold">{catchData.general_location}</p>
              </div>
            </div>

            {catchData.length_inches && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm text-muted-foreground">Length</p>
                  <p className="text-lg font-semibold">{catchData.length_inches} inches</p>
                </div>
              </div>
            )}

            {catchData.girth_inches && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div>
                  <p className="text-sm text-muted-foreground">Girth</p>
                  <p className="text-lg font-semibold">{catchData.girth_inches} inches</p>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {catchData.angler_notes && (
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground mb-2">Notes</p>
              <p className="text-foreground whitespace-pre-wrap">{catchData.angler_notes}</p>
            </div>
          )}

          {/* Video link */}
          {catchData.video_url && (
            <a
              href={catchData.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-primary hover:underline"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Watch Video</span>
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
